import React, { useState } from "react";

import { Button, Box, Typography, Chip, Stack, DialogContent, Dialog, IconButton, CircularProgress, Alert } from "@mui/material";
import { WarehouseConnection } from "@domain/inventory/models/WarehouseConnection";
import { APP_CONFIG } from "../../../../../config/app.config";
import { Add, Close, UploadFile } from "@mui/icons-material";
import { RuleField } from "@domain/inventory/enums/RuleField.enum";
import FileDragDrop from "@shared/ui/common/FileDragDrop";
import DialogHeader from "@shared/ui/dialog/DialogHeader";
import { container } from "@infrastructure/container";
import { useToast } from "@shared/hooks/useToast";

interface FileWarehouseConnectionButtonProps {
    connection: WarehouseConnection;
    onEditConnection: (connection: WarehouseConnection) => void;
}

export const FileWarehouseConnectionButton = ({ connection, onEditConnection }: FileWarehouseConnectionButtonProps) => {

    const [open, setOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const { success, error: showError } = useToast();

    const getRules = (field: RuleField): string | null => {
        const rule = connection.rules.find(rule => rule.field === field);
        if (!rule) {
            return null
        }

        return rule.value as string;
    }

    const getAllowedFileTypes = (): string[] | undefined => {
        const rule = getRules(RuleField.FILE_TYPE);

        switch (rule) {
            case 'csv':
                return ['text/csv'];
            case 'excel':
                return ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
        }
        
        return undefined;
    }

    const handleFileSelect = async (file: File) => {
        setIsUploading(true);
        setUploadError(null);
        
        try {
            const result = await container.inventoryRepository.submitFileMutation(file, connection.id);
            if (result.status === 'error') {
                const errorMessage = result.error?.message || 'Failed to upload file';
                setUploadError(errorMessage);
                showError(errorMessage);
            } else {
                success('File uploaded successfully');
                setOpen(false);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to upload file';
            setUploadError(errorMessage);
            showError(errorMessage);
        } finally {
            setIsUploading(false);
        }
    }

    return (

        <>
            <Button onClick={() => setOpen(true)} sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'center', '&:hover': { backgroundColor: 'transparent' } }}>
                <Box sx={{ display: 'flex', border: `1px dashed ${APP_CONFIG.ui.theme.colors.primary}`, borderRadius: 1, width: 100, height: 70, alignItems: 'center', justifyContent: 'center' }}>
                    <UploadFile />
                </Box>


                <Box flexGrow={1} textAlign="left">
                    <Typography variant="body2" color="text.primary" gutterBottom>Upload your <br />inventory {getRules(RuleField.FILE_TYPE)}</Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Add />
                </Box>
            </Button>

            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogHeader>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography variant="body1">Upload your inventory</Typography>

                        <IconButton onClick={() => setOpen(false)}>
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogHeader>
                <DialogContent>
                    {isUploading ? (
                        <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
                            <CircularProgress size={40} />
                            <Typography variant="body1" color="text.primary">
                                Uploading file...
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Please wait while we process your inventory file
                            </Typography>
                        </Stack>
                    ) : (
                        <>
                            <FileDragDrop 
                                onFileSelect={handleFileSelect} 
                                allowedFileTypes={getAllowedFileTypes()}
                                disabled={isUploading}
                            />

                            {uploadError && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {uploadError}
                                </Alert>
                            )}

                            <Stack flexWrap="wrap" direction="row" alignItems="center" gap={1} mt={2}>
                                {connection.rules.map((rule) => (
                                    <Chip key={rule.field} label={rule.field + ': ' + rule.value} color="default" size="small" />
                                ))}
                            </Stack>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );

};